import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "linkagePillsContainer","selectedList", "linkedCourses",
                    "linkageModal","singleLinkageModal", "preselectedDocumentSet"]

  initialize() {
    this.confirmedIds = new Set()
    this.tempIds = new Set()

    // load any pre-existing values from hidden field
    if (this.hasLinkedCoursesTarget) {
      const v = this.linkedCoursesTarget.value?.trim()
      if (v) {
        v.split(",").filter(Boolean).map(Number).forEach(id => this.confirmedIds.add(id))
      }
    }

    if (window.location.search.includes('course_assessment=true')) {
      this.courseLink();
    } else if (this.hasPreselectedDocumentSetTarget && this.preselectedDocumentSetTarget.value) {
      document.getElementById("hiddenSingleCourseModalBtn").click()
    }

    this.renderPills()
    this.element.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && e.target.tagName === "BUTTON") {
        e.preventDefault()
        e.target.click()
      }
    })
  }

  openCustomCourseModal() {
    this.tempIds = new Set([...this.confirmedIds])
    this.syncModalCheckboxes()
    this.renderModalSelectedList()
  }

  syncModalCheckboxes() {
    if (!this.hasLinkageModalTarget) return
    const checkboxes = this.linkageModalTarget.querySelectorAll("input[name='custom_courses[]']")
    checkboxes.forEach(cb => {
      const id = Number(cb.value)
      cb.checked = this.tempIds.has(id)
    })
  }

  toggleTempSelection(event) {
    const cb = event.target
    const id = Number(cb.value)
    if (cb.checked) this.tempIds.add(id)
    else this.tempIds.delete(id)
    this.renderModalSelectedList()
    const hiddenField = document.getElementById("selected_courses_for_relation")
    if (hiddenField) {
      hiddenField.value = [...this.tempIds].join(",")
    }
  }

  renderModalSelectedList() {
    if (!this.hasSelectedListTarget) return
    const ul = this.selectedListTarget
    ul.innerHTML = ""

    this.tempIds.forEach(id => {
      const label = this.linkageModalTarget.querySelector(`label[for="linkage_custom_course_${id}"]`)
      const name = label ? label.innerText.trim() : `Course ${id}`
      const li = document.createElement("li")
      li.textContent = name
      ul.appendChild(li)
    })
  }

  confirmLink(event) {
    event.preventDefault()

    // Read existing linked courses from hidden field
    const existingIdsField = document.getElementById("already_linked_course_ids")
    const existingIds = existingIdsField ? existingIdsField.value.split(",").map(Number) : []

    this.tempIds.forEach(id => {
      if (!existingIds.includes(id)) {
        this.confirmedIds.add(id)
      }
    })

    if (this.hasLinkedCoursesTarget) {
      this.linkedCoursesTarget.value = [...this.confirmedIds].join(",")
    }
    this.renderPills()

    if (this.hasLinkageModalTarget) {
      const dismissBtn = this.linkageModalTarget.querySelector('[data-bs-dismiss="modal"]')
      if (dismissBtn) {
        dismissBtn.click()
      } else {
        this.linkageModalTarget.classList.remove("show")
        this.linkageModalTarget.style.display = "none"
        document.body.classList.remove("modal-open")
      }
    }
  }

  closeConfirmation(event) {
    this.tempIds = new Set([...this.confirmedIds])
    this.syncModalCheckboxes()
    this.renderModalSelectedList()

    if (this.hasLinkageModalTarget) {
      const dismissBtn = this.linkageModalTarget.querySelector('[data-bs-dismiss="modal"]')
      if (dismissBtn) {
        dismissBtn.click()
      } else {
        this.linkageModalTarget.classList.remove("show")
        this.linkageModalTarget.style.display = "none"
        document.body.classList.remove("modal-open")
      }
    }
  }

  singleCourseLink(event) {
    event.preventDefault()
    this.courseLink();
  }

  courseLink() {
    const preselectedDocumentSet = Number(this.preselectedDocumentSetTarget.value)
    this.confirmedIds.add(preselectedDocumentSet)
    if (this.hasLinkedCoursesTarget) {
      this.linkedCoursesTarget.value = [...this.confirmedIds].join(",")
    }
    this.renderPills()
  }

  closeSingleCourseLink(event) {
    if (this.hasSingleLinkageModalTarget) {
      const dismissBtn = document.getElementById("cancelSingleCourseLinkBtn");
      if (dismissBtn) {
        dismissBtn.click()
      } else {
        this.singleLinkageModalTarget.classList.remove("show")
        this.singleLinkageModalTarget.style.display = "none"
        document.body.classList.remove("modal-open")
      }
    }
  }

  renderPills() {
    if (!this.hasLinkagePillsContainerTarget) return
    const container = this.linkagePillsContainerTarget
    container.innerHTML = ""

    if (this.confirmedIds.size === 0) {
      return
    }

    this.confirmedIds.forEach(id => {
      const label = document.querySelector(`label[for="linkage_custom_course_${id}"]`)
      const text = label ? label.innerText.split("(")[0].trim() : `Course ${id}`

      const pill = document.createElement("div")
      pill.className = "filter-easy-clear-button"
      pill.innerHTML = `
        <span class="me-2">${text}</span>
        <a href="#" class="text-black ms-1" aria-label="Remove"
          data-action="click->confirmation-modal#removePill"
          data-id="${id}">×</a>
      `
      container.appendChild(pill)
    })
  }

  removePill(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = Number(event.currentTarget.dataset.id)

    if (confirm("Do you want to remove the link between this course and Calibrate assessment?")) {
      this.confirmedIds.delete(id)
      if (this.hasLinkedCoursesTarget) {
        this.linkedCoursesTarget.value = [...this.confirmedIds].join(",")
      }
      this.renderPills()
    }
  }

  openLinkageModal(event) {
    const existingIdsField = document.getElementById("already_linked_course_ids")
    const existingIds = existingIdsField ? existingIdsField.value.split(",").filter(Boolean).map(Number) : []
    this.tempIds = new Set(existingIds)
    this.confirmedIds = new Set(existingIds)
    this.syncModalCheckboxes()
    this.renderModalSelectedList()
  }

  confirmCreation(event) {
    event.preventDefault()

    const deliveryId = document.getElementById("delivery_id")?.value
    const selectedCourses = Array.from(
      document.querySelectorAll("#custom_courses_selection input[type='checkbox']:checked")
    ).map(cb => cb.value)

    const formData = new FormData()
    selectedCourses.forEach(id => {
      formData.append("document_set_id[]", id)
    })
    formData.append("assessment_delivery_id", deliveryId)

    const token = document.querySelector("meta[name='csrf-token']")?.content

    fetch("/document_set_delivery_relations", {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token
      },
      body: formData
    })
      .then(response => {
        if (!response.ok) throw new Error("Network response was not ok")
        return response.text()
      })
      .then(html => {
        Turbo.renderStreamMessage(html)
      })
      .catch(error => console.error("Error:", error))
  }
}
